import { useEffect, useState } from 'react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryCounterService from '../../../services/nurseryCounter';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import { parseAmount } from '../../../utils/counterCash';
import type { InvoiceCashPaymentResult, OpenTill } from '../../../types/nurseryCounter';

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

export function InvoiceCashPaymentDialog({ invoiceId, invoiceNumber, balance, onClose, onDone }: {
  invoiceId: number;
  invoiceNumber: string;
  balance: number;
  onClose: () => void;
  onDone: (result: InvoiceCashPaymentResult) => void;
}) {
  const [tills, setTills] = useState<OpenTill[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [tillsLoading, setTillsLoading] = useState(true);
  const [tillsError, setTillsError] = useState<string | null>(null);
  const [cash, setCash] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    nurseryCounterService.listTills().then((res) => {
      if (!active) return;
      if (res.success) {
        setTills(res.data.open);
        setSessionId(String(res.data.open[0]?.id ?? ''));
      } else setTillsError(errorMessage(res, 'Could not load open tills'));
      setTillsLoading(false);
    }).catch((e: unknown) => {
      if (!active) return;
      setTillsError(errorMessage(e, 'Could not load open tills'));
      setTillsLoading(false);
    });
    return () => { active = false; };
  }, []);

  const tendered = parseAmount(cash);
  const applied = tendered == null ? 0 : Math.min(tendered, balance);
  const change = tendered == null ? 0 : Math.max(0, tendered - balance);

  const save = async () => {
    if (!sessionId || tendered == null || tendered <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.takeInvoiceCash(invoiceId, Number(sessionId), tendered);
      if (res.success && res.data) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, 'Could not record the cash payment'));
    } catch (e) {
      setError(errorMessage(e, 'Could not record the cash payment'));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={`Take cash for ${invoiceNumber}`}
      subtitle={`Balance owing: ${money(balance)}. A partial payment is allowed.`}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || tillsLoading || !sessionId || tendered == null || tendered <= 0}>
            {busy ? 'Recording…' : `Record ${money(applied)}`}
          </Button>
        </>
      }
    >
      <div>
        <label htmlFor="invoice-cash-till" className="block text-sm font-medium text-neutral-700 mb-1">Open till</label>
        <select id="invoice-cash-till" className={input} value={sessionId} disabled={tillsLoading || tills.length === 0}
          onChange={(e) => setSessionId(e.target.value)}>
          <option value="">{tillsLoading ? 'Loading tills…' : 'Choose a till…'}</option>
          {tills.map((till) => <option key={till.id} value={till.id}>{till.till_name}</option>)}
        </select>
        {tillsError && <p className="mt-1 text-sm text-red-700 break-words" role="alert">{tillsError}</p>}
        {!tillsLoading && !tillsError && tills.length === 0 && (
          <p className="mt-1 text-sm text-amber-800 break-words">
            There is no open till. Open one from <a href="/nursery/tills" className="underline">Tills</a> before taking cash.
          </p>
        )}
      </div>
      <div>
        <label htmlFor="invoice-cash-tendered" className="block text-sm font-medium text-neutral-700 mb-1">Cash received</label>
        <input id="invoice-cash-tendered" type="text" inputMode="decimal" value={cash}
          onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      {tendered != null && tendered > 0 && (
        <dl className="rounded-lg bg-neutral-50 p-3 text-sm space-y-1">
          <div className="flex justify-between gap-3"><dt>Applied to invoice</dt><dd className="font-medium">{money(applied)}</dd></div>
          {change > 0 && <div className="flex justify-between gap-3"><dt>Change to give</dt><dd className="font-medium">{money(change)}</dd></div>}
        </dl>
      )}
      <p className="text-xs text-neutral-600 break-words">
        The receipt is posted to this invoice in Xero through the cash-on-hand account. When you bank cash from the till, record it there too; the Xero transfer can then be matched to the bank-feed deposit.
      </p>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}
